<template>
  <div id="question">
    <main>
      <div v-if="current" :class="`content ${current.meta.type}`">
        <h2
          v-if="current.translations.pt_BR.title"
          id="questions-h2-title"
          class="h1 color--grey questions__title"
        >
          <strong>{{ getTitle }}</strong>
        </h2>
        <div>
          <template v-if="isComponentBtn">
            <QuestionButtons
              class="buttons-question"
              :options="hasOptions"
              @input="questionInputChange"
              :value="selectedValue"
              :type="1"
            />
          </template>

          <div v-else>
            <div class="flex flex-justify-content--center">
              <QuestionInput
                :inputId="`questions-input-${current.slug}`"
                :type="current.meta.type"
                v-model="selectedValue"
                :mask-type="maskType"
                :key="current.id"
                :error="emptyErrorMessage"
              />
              <span class="suffix font__size--4 padding-x-5">
                {{ current.meta.question ? current.meta.question.suffix : null }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </main>

    <div
      v-if="showBtnContinue"
      class="flex flex-justify-content--center flex-justify-content--center"
    >
      <VButton
        id="questions-btn-continue"
        text="Continuar"
        :disabled="!minMax || !optionalInput"
        @click.native="set"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import formatTime from '@/utils/helpers/maskers/time/timeZoneFullFormat';
import validateTime from '@/utils/helpers/validation/validateTime';
import { BrandEnum } from '@/utils/enums/brandsEnum';
import { setSelectedValueMask } from './partials';
import { dataLayersMixin } from '@/utils/mixins';
import debounce from 'lodash/debounce';

const CURRENT_QUESTION_EMPTY = {
  id: '',
  slug: '',
  translations: {
    pt_BR: {
      title: null,
    },
  },
  meta: {
    type: null,
    gender: null,
    required_for: null,
    required_if: {
      question: null,
      value: null,
    },
    question: {
      options: null,
      min: null,
      max: null,
      suffix: null,
    },
    categories: [null],
  },
};

export default {
  name: 'Questions',
  data() {
    return {
      selectedValue: '',
      index: 0,
      answers: [],
      activeBtn: false,
      current: { ...CURRENT_QUESTION_EMPTY },
      medicationsAnswerDraft: '',
      questionsToConclude: ['last_meal_date', 'last_meal_time'],
    };
  },
  mixins: [dataLayersMixin],

  async created() {
    if (!this.questionnaire.received.length) {
      await this.getQuestionnaire();
    }
    await this.getAdmissionQuestionsById();
    await this.checkHasPreviousQuestionsAnswered();
    this.medicationsAnswerDraft = this.questionnaire.medications || '';
    this.questionnaire.toSend = [];

    if (this.questionnaire.received.length) {
      const mealQuestions = this.questionnaire.received.filter((question) =>
        this.questionsToConclude.includes(question.slug),
      );

      this.setQuestionnaire(mealQuestions);
      this.setCurrentQuestion(this.questionnaire.received[0]);
      this.sendCurrentQuestionToAnalytics(this.questionnaire.received[0]);
    } else {
      return this.goNextStep('end_flow_success');
    }
  },
  watch: {
    selectedValue(newValue, oldValue) {
      const type = this.current.meta.type;
      const suffix = this.current.meta.question?.suffix;

      const maskedSelectedValue = setSelectedValueMask(newValue, oldValue, type, suffix);

      if (maskedSelectedValue) {
        this.selectedValue = maskedSelectedValue;
      }

      if (this.current.slug === 'medications') {
        this.debounceFieldChange()(newValue);
      }
    },
  },
  methods: {
    ...mapActions([
      'getAdmissionQuestionsById',
      'updateAdmissionQuestions',
      'getQuestionnaire',
      'getAdmissionQuestions',
      'validateHealthInsuranceVariables',
    ]),
    ...mapMutations({
      setMedications: 'SET_MEDICATIONS',
      setQuestionnaire: 'SET_QUESTIONNAIRE',
      setToSend: 'SET_TOSEND',
    }),
    debounceFieldChange() {
      return debounce((medications) => {
        this.setMedications(medications);
      }, 3000);
    },
    sendCurrentQuestionToAnalytics(question) {
      const paymentType =
        this.currentAdmissionState.payments &&
        this.currentAdmissionState.payments.length &&
        this.currentAdmissionState.payments[0].details.type;

      this.mostCommonPagesAnalytics({
        step: 'perguntas_restritivas',
        screen: question.slug,
        complementaryObject: {
          ...(!this.questionnaire.toSend.length && this.$route.params.answer
            ? { previous_choice: this.$route.params.answer }
            : null),
          ...(paymentType === 'PRIVATE'
            ? {
                payment: { type: 'particular' },
              }
            : {
                payment: {
                  type: 'convênio',
                },
                insurance: {
                  brand: this.healthInsuranceVariables.nomeCliente,
                  plan: this.getInsurancePlan,
                },
              }),
        },
      });
    },
    questionInputChange(value) {
      this.selectedValue = value;

      // seTimeout for user see its touch answer
      setTimeout(() => {
        this.set();
      }, 400);
    },

    async set() {
      await this.controlQuestion();

      this.setCurrentQuestion({ ...CURRENT_QUESTION_EMPTY });

      this.$nextTick(() => {
        this.next();
      });
    },

    setCurrentQuestion(question) {
      this.current = question;

      if (this.current) {
        const currentQuestion = this.admissionQuestions.find(
          (el) => el.question === this.current.slug,
        );

        if (this.current.slug === 'medications') {
          if (!currentQuestion) {
            this.selectedValue = this.medicationsAnswerDraft || '';
            return;
          }
          this.selectedValue =
            currentQuestion.answer !== 'N/A' ? currentQuestion.answer : this.medicationsAnswerDraft;
          return;
        }
        this.selectedValue = currentQuestion ? currentQuestion.answer : '';
      }
    },

    next() {
      // if last question?
      if (this.questionnaire.received.length <= ++this.index) {
        this.sendQuestions();
      } else {
        this.currentQuestion();
      }
    },

    async currentQuestion() {
      const { received, toSend } = this.questionnaire;
      const requiredIf = received[this.index].meta.required_if
        ? received[this.index].meta.required_if[0]
        : false;
      if (requiredIf.value) {
        const question = toSend.find((el) => el.question === requiredIf.question);
        if (question.answer !== requiredIf.value.toString()) {
          if (question.answer === 'no' && received[this.index].meta.type === 'text') {
            const questionModel = {
              uuid: '',
              question: received[this.index].slug,
              answer: 'N/A',
            };
            const questionTarget = await this.admissionQuestions.find(
              (el) => el.question === questionModel.question,
            );
            questionModel.uuid = questionTarget ? questionTarget.uuid : '';
            this.questionnaire.toSend.push(questionModel);
          }
          ++this.index;
        }
      }

      if (this.index >= received.length) {
        this.sendQuestions();
      } else {
        this.setCurrentQuestion(received[this.index]);
        this.sendCurrentQuestionToAnalytics(received[this.index]);
      }
    },

    async controlQuestion() {
      const questionModel = {
        uuid: '',
        question: '',
        answer: '',
      };
      const questionsPayload = this.admissionQuestions;

      // sets question slug
      questionModel.question = this.current.slug;

      // sets question uuid if exists
      const targetUuid = await questionsPayload.find(
        (el) => el.question === questionModel.question,
      );
      questionModel.uuid = targetUuid ? targetUuid.uuid : '';

      // sets question answer
      questionModel.answer = this.selectedValue;

      if (this.current.meta.type === 'time') {
        questionModel.answer = formatTime(this.selectedValue);
      }

      if (!questionModel.answer && this.current.meta.type === 'text') {
        questionModel.answer = 'N/A';
      }

      if (this.current.slug === 'medications') {
        this.setMedications(questionModel.answer);
      }

      // controll questions push
      this.setToSend(questionModel);
    },
    async sendQuestions() {
      let mealDateAnswer = '';
      let mealTimeAnswer = '';
      let mealType = 'CAFE_MANHA';

      const inStore = await this.questionnaire.toSend.map((item) => {
        if (item.question === 'last_meal_date') mealDateAnswer = item.answer;
        if (item.question === 'last_meal_time') mealTimeAnswer = item.answer;
        return item.question;
      });

      const hour = mealTimeAnswer.toString().substring(0, 2);
      if (hour >= 11 && hour <= 16) {
        mealType = 'ALMOCO';
      } else if ((hour >= 17 && hour < 23) || (hour >= 0 && hour <= 2)) {
        mealType = 'JANTAR';
      }

      // set required "last meal" questions
      if (mealTimeAnswer || mealDateAnswer) {
        const lastMealRequired = [
          {
            uuid: '',
            question: 'last_meal_type',
            answer: mealType,
          },
          {
            uuid: '',
            question: 'last_meal_instant',
            answer: `${mealDateAnswer}T${mealTimeAnswer}`,
          },
        ];

        lastMealRequired.forEach((item) => {
          const questionTarget = this.admissionQuestions.find(
            (el) => el.question === item.question,
          );
          item.uuid = questionTarget ? questionTarget.uuid : '';
          this.setToSend(item);
        });
      }

      const diff = this.admissionQuestions.filter(
        (question) =>
          !inStore.includes(question.question) &&
          question.question !== 'last_meal_type' &&
          question.question !== 'last_meal_instant',
      );
      if (diff.length > 0) {
        diff.forEach((e) => {
          this.setToSend({
            uuid: e.uuid,
            answer: e.answer,
            question: e.question,
          });
        });
      }
      await this.updateAdmissionQuestions();

      return this.goNextStep('end_flow_success');
    },
    async checkHasPreviousQuestionsAnswered() {
      const restrictiveQuestions = [...this.questionnaire.received];
      const awaitedQuestions = this.questionnaire.received.map((item) => item.slug);
      const answeredOnAdmissionQuestions =
        this.admissionQuestions &&
        this.admissionQuestions.length &&
        this.admissionQuestions.map((item) => item.question);

      const hasResponsedQuestion =
        answeredOnAdmissionQuestions &&
        answeredOnAdmissionQuestions.filter((question) => awaitedQuestions.includes(question));

      if (!answeredOnAdmissionQuestions?.length) {
        return;
      }

      if (this.currentAdmissionState.checkInDone) {
        if (hasResponsedQuestion && hasResponsedQuestion?.length === awaitedQuestions?.length) {
          return this.goNextStep('end_flow_success');
        }

        if (answeredOnAdmissionQuestions && answeredOnAdmissionQuestions?.length) {
          let filteredQuestions = restrictiveQuestions.filter(
            (question) => !answeredOnAdmissionQuestions.includes(question.slug),
          );

          if (answeredOnAdmissionQuestions.includes('taking_medications')) {
            const isTakingMedication = this.admissionQuestions.find(
              (item) => item.question === 'taking_medications',
            );
            if (isTakingMedication.answer === 'false' || isTakingMedication.answer === false) {
              filteredQuestions = filteredQuestions.filter(
                (question) => question.slug !== 'medications',
              );
            }
          }
          if (answeredOnAdmissionQuestions.includes('pregnant')) {
            const isPregnant = this.admissionQuestions.find((item) => item.question === 'pregnant');
            if (isPregnant) {
              filteredQuestions = filteredQuestions.filter(
                (question) => question.slug !== 'pregnant_duration',
              );
            }
          }
          if (!filteredQuestions.length) {
            return this.goNextStep('end_flow_success');
          }

          await this.setQuestionnaire(filteredQuestions);
        }

        if (awaitedQuestions.length > hasResponsedQuestion.length) {
          const selectedQuestions = this.admissionQuestions.filter((item) =>
            hasResponsedQuestion.includes(item.question),
          );
          selectedQuestions.forEach((answeredQuestion) => {
            this.setToSend(answeredQuestion);
          });
        }
      }
    },
  },
  computed: {
    ...mapState({
      currentAdmissionState: ({ currentAdmission }) => currentAdmission.data,
      currentAdmissionPayment: ({ currentAdmission }) => currentAdmission.data.payments[0],
      admissionQuestions: ({ currentAdmission }) => currentAdmission.data.questions || [],
      questionnaire: ({ questionnaire }) => questionnaire,
      isAdmissionAuthorized: ({ global }) => global.isAdmissionAuthorized,
      healthInsuranceVariables: ({ healthInsurance }) =>
        healthInsurance.data.healthInsuranceVariables,
    }),
    ...mapGetters({
      haveScheduledExams: 'haveScheduledExams',
      getBrandUnitIdVariables: 'getBrandUnitIdVariables',
      isScheduledAdmission: 'global/isScheduledAdmission',
      isFlowStartedWithPaymentChoice: 'global/isFlowStartedWithPaymentChoice',
      backOfficeDone: 'backOfficeDone',
      getInsurancePlan: 'getInsurancePlan',
    }),
    getTitle() {
      const title = this.current.translations.pt_BR.title;

      return `${title.charAt(0).toUpperCase()}${title.slice(1).toLowerCase()}`;
    },
    getMainExam() {
      // Se a marca for Lamina SC, vamos retornar Vitamina D
      if (this.getBrandUnitIdVariables.brandId === BrandEnum.LAMINA_SC) {
        return '1058';
      }

      // By default, return exame code for hemogram
      return '1307';
    },
    isComponentBtn() {
      switch (this.current.meta.type) {
        case 'boolean':
        case 'select':
          return true;
        default:
          return false;
      }
    },

    hasOptions() {
      return this.current.meta.question?.options || undefined;
    },

    maskType() {
      const meta = this.current.meta;

      if (meta.type === 'picker') {
        return meta.question.suffix === 'm' ? 'height' : null;
      }

      return meta.type;
    },

    minMax() {
      const meta = this.current.meta;

      if (meta.question?.max) {
        const min = meta.question.min;
        const max = meta.question.max;
        const selectedValueNumber = Number(this.selectedValue);

        return selectedValueNumber > min && selectedValueNumber < max;
      }
      if (meta.type === 'time') {
        // exclusive for "qual horário...'
        return validateTime(this.selectedValue);
      }

      return true;
    },

    optionalInput() {
      return this.selectedValue ? true : this.current.meta.type === 'text';
    },

    emptyErrorMessage() {
      if (this.optionalInput && !this.minMax) {
        if (this.current.meta.type === 'time') {
          // exclusive for "qual horário...'
          return 'Insira um horário válido';
        }
        return 'Valor não permitido';
      }
      return null;
    },

    showBtnContinue() {
      const arrTypeBtnHide = ['select', 'boolean'];
      const type = this.current.meta.type;

      return arrTypeBtnHide.indexOf(type) === -1;
    },
  },
};
</script>

<style lang="scss" scoped>
.suffix {
  margin-left: 6px;
  color: #747679;
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
  align-self: center;
  margin-top: 7px;
}

#question {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-bottom: 24px;
  justify-content: space-between;

  main {
    height: 100%;

    .content {
      height: 100%;
      display: flex;

      flex: {
        grow: 1;
        wrap: wrap;
        direction: column;
      }

      justify-content: center;
      align-content: center;
    }

    .questions__title {
      margin-bottom: 16px;

      strong {
        color: #6e7788;
      }
    }
  }
}
</style>

<style lang="scss">
.buttons-question {
  .question__buttons {
    button {
      border-color: #e1e4ea;
    }
  }
}
</style>
